import { useRender } from "@base-ui/react/use-render";
import type { IconData } from "@loot-os/icons/types";
import type { ReactElement } from "react";

import { Icon } from "../core/Icon.js";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { useLinkRender } from "../foundations/linkContext.js";
import { useLayout } from "../foundations/themeContext.js";

export interface NavLinkProps {
  label: string;
  href?: string;
  /** The page the shopper is on; marks it aria-current and lifts it. */
  current?: boolean;
  /** Leads the label where the theme draws header links with icons (`layout.navLinks`). */
  icon?: IconData;
  render?: ReactElement;
}

/** One product link in TopNav: its label, led by its icon on a theme that asks for one. */
export function NavLink({ label, href, current = false, icon, render }: NavLinkProps) {
  const layout = useLayout();
  const withIcon = icon && layout.navLinks === "icon";
  return useRender({
    render: useLinkRender(render),
    defaultTagName: "a",
    props: {
      href,
      "aria-current": current ? "page" : undefined,
      className: cn(
        // Primary text at rest: links sit on glass over the stage's brightest light, where secondary
        // text falls under 4.5:1. The current page is marked by its fill and aria-current.
        "inline-flex h-control-sm items-center gap-2 rounded-control px-3.5 font-body text-body-b1-strong text-text-primary hover:bg-glass-active",
        current && "bg-glass-active",
        FOCUS_RING,
      ),
      children: withIcon ? (
        <>
          {/* Secondary until it is the page the shopper is on; it plays on hover where icons do. */}
          <Icon
            icon={icon}
            trigger="hover"
            className={current ? "text-text-primary" : "text-text-secondary"}
          />
          {label}
        </>
      ) : (
        label
      ),
    },
  });
}
